import { useMutation } from '@apollo/client';

import {
  ASSESSMENT_STAGE_FIELDS,
  COMPLETE_ASSESSMENT_STAGE,
  CREATE_ASSESSMENT_STAGE,
  MOVE_ASSESSMENT_STAGE,
  REMOVE_ASSESSMENT_STAGE,
  UPDATE_ASSESSMENT_STAGE
} from '../queries/assessment-stage.queries';
import { createCacheModifier, removeCacheModifier } from '../cache/basic.cache';
import { getAuthUser } from './auth.service';

const STRUCTURE_REFETCH_QUERIES = ['GetAssessmentStageBuilder', 'GetManageAgeGroups', 'GetManageAgeGroupGraphQL', 'GetAgeGroups', 'GetAgeGroupsInAssessment'];

interface CreateAssessmentStageProps {
  ageGroupId: string;
  order: number;
  type: string;
  config: object;
}

export const buildCompleteAssessmentStage = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(COMPLETE_ASSESSMENT_STAGE);

  return {
    ...rest,
    completeAssessmentStage: (_id: string) =>
      mutation({
        variables: { _id },
        update: (cache, {data}) => {
          if (data) {
            const createdDoc = data.completeAssessmentStage;

            cache.modify({
              fields: {
                assessmentStages: createCacheModifier({
                  cache,
                  createdDoc,
                  fragmentName: 'AssessmentStageFields',
                  fragment: ASSESSMENT_STAGE_FIELDS,
                  modelName: 'AssessmentStage',
                  append: true
                })
              }
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          completeAssessmentStage: {
            __typename: 'AssessmentStage',
            _id,
            isCompleted: true
          }
        }
      })
  };
};

export const buildCreateAssessmentStage = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(CREATE_ASSESSMENT_STAGE, {
    refetchQueries: STRUCTURE_REFETCH_QUERIES,
    awaitRefetchQueries: true
  });

  return {
    ...rest,
    createAssessmentStage: (data: CreateAssessmentStageProps) =>
      mutation({
        variables: { data: { ...data, assessmentId: user?.settings.activeAssessmentId } },
        update: (cache, { data }) => {
          if (data) {
            const createdDoc = data.createAssessmentStage;

            cache.modify({
              fields: {
                assessmentStages: createCacheModifier({
                  cache,
                  createdDoc,
                  fragmentName: 'AssessmentStageFields',
                  fragment: ASSESSMENT_STAGE_FIELDS,
                  modelName: 'AssessmentStage',
                  append: true
                })
              }
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createAssessmentStage: {
            __typename: 'AssessmentStage',
            ...data,
            assessmentId: user?.settings.activeAssessmentId
          }
        }
      })
  };
};

interface UpdateAssessmentStageProps {
  order?: number;
  title?: string;
  config?: object;
}

export const optimisticallyMoveAssessmentStage = (
  cache: any,
  stage: Pick<IAssessmentStage, '_id' | 'order'>,
  direction: 'UP' | 'DOWN'
) => {
  let moved = false;

  cache.modify({
    fields: {
      assessmentStages: (existingRefs = [], { readField }: any) => {
        if (moved) return existingRefs;

        const orderedRefs = [...existingRefs].sort(
          (left, right) => readField('order', left) - readField('order', right)
        );
        const currentIndex = orderedRefs.findIndex(ref => readField('_id', ref) === stage._id);
        const adjacentRef = orderedRefs[currentIndex + (direction === 'UP' ? -1 : 1)];

        if (currentIndex < 0 || !adjacentRef) return existingRefs;

        const adjacentId = readField('_id', adjacentRef);
        const adjacentOrder = readField('order', adjacentRef);

        cache.modify({
          id: `AssessmentStage:${stage._id}`,
          fields: { order: () => adjacentOrder }
        });
        cache.modify({
          id: `AssessmentStage:${adjacentId}`,
          fields: { order: () => stage.order }
        });
        moved = true;

        return existingRefs;
      }
    }
  });
};

export const buildUpdateAssessmentStage = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UPDATE_ASSESSMENT_STAGE);

  return {
    ...rest,
    updateAssessmentStage: (_id: string, data: UpdateAssessmentStageProps) =>
      mutation({
        variables: { _id, data },
        optimisticResponse: {
          __typename: 'Mutation',
          updateAssessmentStage: {
            __typename: 'AssessmentStage',
            _id,
            data
          }
        },
        update: (cache, {data: updatedData}) => {
          if (updatedData.updateAssessmentStage)
            cache.modify({
              fields: {
                assessmentStages: createCacheModifier({
                  cache,
                  createdDoc: {...updatedData.updateAssessmentStage.data, _id},
                  fragmentName: 'AssessmentStageFields',
                  fragment: ASSESSMENT_STAGE_FIELDS,
                  modelName: 'AssessmentStage',
                  append: true
                })
              }
            });
        }
      })
  };
};

export const buildMoveAssessmentStage = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(MOVE_ASSESSMENT_STAGE, {
    refetchQueries: STRUCTURE_REFETCH_QUERIES,
    awaitRefetchQueries: true
  });

  return {
    ...rest,
    moveAssessmentStage: (stage: IAssessmentStage, direction: 'UP' | 'DOWN') =>
      mutation({
        variables: { _id: stage._id, direction, expectedOrder: stage.order },
        optimisticResponse: {
          __typename: 'Mutation',
          moveAssessmentStage: {
            __typename: 'AssessmentStage',
            _id: stage._id
          }
        },
        update: cache => optimisticallyMoveAssessmentStage(cache, stage, direction)
      })
  };
};

export const buildRemoveAssessmentStage = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(REMOVE_ASSESSMENT_STAGE, {
    refetchQueries: STRUCTURE_REFETCH_QUERIES,
    awaitRefetchQueries: true
  });

  return {
    ...rest,
    removeAssessmentStage: (_id: string) =>
      mutation({
        variables: { _id },
        update: cache => {
          cache.modify({
            fields: {
              assessmentStages: removeCacheModifier(cache, _id)
            }
          });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          removeAssessmentStage: {
            __typename: 'DeleteResponse',
            _id
          }
        }
      })
  };
};
